// Minimal Supabase client over the REST API: auth (email and password), PostgREST and RPC.
// Kept small and dependency-free; the session lives in localStorage.
import { SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY } from '@archviewvr/config';

export interface User { id: string; email: string; user_metadata?: Record<string, unknown> }
export interface Session { access_token: string; refresh_token: string; expires_at: number; user: User }

const STORE = 'avr-session';
let session: Session | null = load();
const listeners = new Set<(s: Session | null) => void>();

function load(): Session | null {
  try { const s = localStorage.getItem(STORE); return s ? (JSON.parse(s) as Session) : null; } catch { return null; }
}
function save(s: Session | null): void {
  session = s;
  try { if (s) localStorage.setItem(STORE, JSON.stringify(s)); else localStorage.removeItem(STORE); } catch { /* storage off */ }
  listeners.forEach((fn) => fn(s));
}
export const onAuthChange = (fn: (s: Session | null) => void) => { listeners.add(fn); return () => listeners.delete(fn); };
export const currentSession = () => session;

export class SupaError extends Error {
  constructor(message: string, public status: number, public code?: string) { super(message); }
}

async function readError(res: Response): Promise<SupaError> {
  let msg = `Request failed (${res.status})`, code: string | undefined;
  try {
    const j = await res.json();
    msg = j.msg || j.message || j.error_description || j.error || msg;
    code = j.code || j.error_code;
  } catch { /* not json */ }
  // friendlier wording for the common auth messages
  if (/invalid login credentials/i.test(msg)) msg = 'That email and password don’t match.';
  if (/email not confirmed/i.test(msg)) msg = 'Confirm your email first. Check your inbox for the link.';
  if (/user already registered/i.test(msg)) msg = 'There’s already an account with that email. Sign in instead.';
  return new SupaError(msg, res.status, code);
}

function sessionFrom(j: any): Session {
  return {
    access_token: j.access_token,
    refresh_token: j.refresh_token,
    expires_at: j.expires_at ?? Math.floor(Date.now() / 1000) + (j.expires_in ?? 3600),
    user: j.user,
  };
}

async function authPost(path: string, body: unknown): Promise<any> {
  const res = await fetch(`${SUPABASE_URL}/auth/v1/${path}`, {
    method: 'POST',
    headers: { apikey: SUPABASE_PUBLISHABLE_KEY, 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await readError(res);
  return res.status === 204 ? null : res.json();
}

const redirectTo = () => `${location.origin}/`;

/** Creates an account. Returns true when the user must confirm their email first. */
export async function signUp(email: string, password: string, name: string): Promise<boolean> {
  const j = await authPost(`signup?redirect_to=${encodeURIComponent(redirectTo())}`, { email, password, data: { name } });
  if (j?.access_token) { save(sessionFrom(j)); return false; }
  return true;
}

export async function signIn(email: string, password: string): Promise<void> {
  save(sessionFrom(await authPost('token?grant_type=password', { email, password })));
}

export async function sendReset(email: string): Promise<void> {
  await authPost(`recover?redirect_to=${encodeURIComponent(redirectTo())}`, { email });
}

export async function setPassword(password: string): Promise<void> {
  await rawFetch('/auth/v1/user', { method: 'PUT', body: JSON.stringify({ password }), headers: { 'content-type': 'application/json' } });
}

export async function signOut(): Promise<void> {
  const s = session;
  save(null);
  if (s) fetch(`${SUPABASE_URL}/auth/v1/logout`, { method: 'POST', headers: { apikey: SUPABASE_PUBLISHABLE_KEY, authorization: `Bearer ${s.access_token}` } }).catch(() => {});
}

/**
 * Email links (confirm, password reset) come back with the session in the URL hash.
 * Returns the link type ("signup", "recovery") when one was handled.
 */
export async function handleAuthRedirect(): Promise<string | null> {
  const h = location.hash.replace(/^#/, '');
  if (!h.includes('access_token=') && !h.includes('error_description=')) return null;
  const q = new URLSearchParams(h);
  history.replaceState(null, '', location.pathname + location.search);
  if (q.get('error_description')) throw new SupaError(q.get('error_description')!.replace(/\+/g, ' '), 400);
  const token = q.get('access_token')!;
  const res = await fetch(`${SUPABASE_URL}/auth/v1/user`, { headers: { apikey: SUPABASE_PUBLISHABLE_KEY, authorization: `Bearer ${token}` } });
  if (!res.ok) throw await readError(res);
  save(sessionFrom({ access_token: token, refresh_token: q.get('refresh_token'), expires_in: Number(q.get('expires_in') ?? 3600), user: await res.json() }));
  return q.get('type');
}

let refreshing: Promise<void> | null = null;
async function freshToken(): Promise<string | null> {
  if (!session) return null;
  if (session.expires_at - 60 > Date.now() / 1000) return session.access_token;
  refreshing ??= authPost('token?grant_type=refresh_token', { refresh_token: session.refresh_token })
    .then((j) => save(sessionFrom(j)))
    .catch(() => save(null))
    .finally(() => { refreshing = null; });
  await refreshing;
  return session?.access_token ?? null;
}

/** The current access token, refreshed if needed. Used for /api calls too. */
export const accessToken = freshToken;

async function rawFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const token = await freshToken();
  const headers = new Headers(init.headers);
  headers.set('apikey', SUPABASE_PUBLISHABLE_KEY);
  if (token) headers.set('authorization', `Bearer ${token}`);
  const res = await fetch(`${SUPABASE_URL}${path}`, { ...init, headers });
  if (res.status === 401 && session) save(null);
  if (!res.ok) throw await readError(res);
  return res;
}

/** GET /rest/v1/<table>?<query> */
export async function select<T = any>(table: string, query = ''): Promise<T[]> {
  return (await rawFetch(`/rest/v1/${table}${query ? `?${query}` : ''}`)).json();
}

/** POST rows, returning the inserted rows. */
export async function insert<T = any>(table: string, rows: object | object[]): Promise<T[]> {
  const res = await rawFetch(`/rest/v1/${table}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json', prefer: 'return=representation' },
    body: JSON.stringify(rows),
  });
  return res.json();
}

/** PATCH rows matching a filter such as "id=eq.<uuid>". */
export async function update<T = any>(table: string, filter: string, values: object): Promise<T[]> {
  const res = await rawFetch(`/rest/v1/${table}?${filter}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json', prefer: 'return=representation' },
    body: JSON.stringify(values),
  });
  return res.json();
}

export async function remove(table: string, filter: string): Promise<void> {
  await rawFetch(`/rest/v1/${table}?${filter}`, { method: 'DELETE' });
}

export async function rpc<T = any>(fn: string, args: object): Promise<T> {
  const res = await rawFetch(`/rest/v1/rpc/${fn}`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(args) });
  return res.json();
}
